import React, { useState, useEffect } from 'react';

const ContactSalesPage = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleMobileMenuClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <>
      <style>{`
        html, body {
          background: #0a0a0a;
          margin: 0;
          padding: 0;
        }
      `}</style>
      <div className="min-h-screen bg-[#0a0a0a] text-white scroll-smooth [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-white/10 [&::-webkit-scrollbar-thumb]:bg-gradient-to-b [&::-webkit-scrollbar-thumb]:from-[#8b5cf6] [&::-webkit-scrollbar-thumb]:to-[#e07a5f] [&::-webkit-scrollbar-thumb]:rounded">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-[1000] py-4 px-4 sm:px-8 transition-all duration-300 ease-in-out bg-[#0a0a0a] ${scrollY > 50 ? 'border-b border-white/10' : ''}`}>
          <div className="flex justify-between items-center max-w-screen-lg mx-auto h-[60px]">
            <a href="https://app.whydiditdie.com/" className="flex items-center gap-3 no-underline hover:-translate-y-0.5 transition-transform">
              <span className="text-3xl sm:text-4xl">🕵️</span>
              <span className="text-xl sm:text-2xl font-black text-[#e07a5f] tracking-tight">WhyDidItDie</span>
            </a>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-10">
              <a href="/how-it-works" className="text-white/90 text-base font-medium hover:text-[#e07a5f] transition-colors no-underline">How It Works</a>
              <a href="/features" className="text-white/90 text-base font-medium hover:text-[#e07a5f] transition-colors no-underline">Features</a>
              <a href="/pricing" className="text-white/90 text-base font-medium hover:text-[#e07a5f] transition-colors no-underline">Pricing</a>
              <a href="/#demo" className="text-white/90 text-base font-medium hover:text-[#e07a5f] transition-colors no-underline">Demo</a>
              <div className="flex items-center gap-4">
                <a href="/login" className="bg-transparent border-2 border-white/20 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:border-[#e07a5f] hover:text-[#e07a5f] transition-colors no-underline">Login</a>
                <a href="/signup" className="bg-[#e07a5f] text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-[#ff6b6b] transition-colors no-underline">Get Started</a>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden flex items-center justify-center bg-white/10 border border-white/20 text-white w-11 h-11 rounded-lg text-lg hover:bg-[#e07a5f]/20 hover:border-[#e07a5f] transition-colors"
            >
              {isMenuOpen ? '✕' : '☰'}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden absolute top-full left-0 right-0 bg-[#0a0a0a] p-8">
              <div className="flex flex-col gap-6">
                <a href="/how-it-works" onClick={handleMobileMenuClick} className="text-white/90 text-lg hover:text-[#e07a5f] transition-colors no-underline">How It Works</a>
                <a href="/features" onClick={handleMobileMenuClick} className="text-white/90 text-lg hover:text-[#e07a5f] transition-colors no-underline">Features</a>
                <a href="/pricing" onClick={handleMobileMenuClick} className="text-white/90 text-lg hover:text-[#e07a5f] transition-colors no-underline">Pricing</a>
                <a href="/#demo" onClick={handleMobileMenuClick} className="text-white/90 text-lg hover:text-[#e07a5f] transition-colors no-underline">Demo</a>
                <a href="/login" onClick={handleMobileMenuClick} className="text-white/90 text-lg hover:text-[#e07a5f] transition-colors no-underline">Login</a>
                <a href="/signup" onClick={handleMobileMenuClick} className="bg-[#e07a5f] text-white px-5 py-2.5 rounded-lg text-base font-bold hover:bg-[#ff6b6b] transition-colors no-underline text-center">Get Started</a>
              </div>
            </div>
          )}
        </nav>

        {/* Main Content */}
        <div className="flex items-center justify-center min-h-screen p-4 sm:p-8 mt-20">
          <div className="w-full max-w-screen-sm text-center">
            {/* Header */}
            <div className="mb-12">
              <div className="inline-block bg-[#8b5cf6]/10 border border-[#8b5cf6]/30 rounded-xl px-4 py-2 mb-4 text-sm font-bold text-[#8b5cf6]">
                💬 Contact Sales
              </div>
              <h1 className="text-4xl sm:text-5xl font-black mb-4 bg-gradient-to-r from-white via-[#8b5cf6] to-[#e07a5f] text-transparent bg-clip-text">
                Let's Talk
              </h1>
              <p className="text-base sm:text-lg text-white/70 mb-8">
                Ready for enterprise-level campaign forensics? Get in touch and we'll create a custom solution for your needs.
              </p>
            </div>

            {/* Contact Methods */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Email */}
              <div className="p-8 bg-[#e07a5f]/10 rounded-xl border border-[#e07a5f]/20">
                <div className="text-5xl mb-4">📧</div>
                <h3 className="text-xl font-bold mb-2 text-[#e07a5f]">Email Us</h3>
                <a 
                  href="mailto:sales@whydiditdie.com"
                  className="text-[#e07a5f] text-lg font-bold no-underline hover:text-[#ff6b6b] transition-colors"
                >
                  sales@whydiditdie.com
                </a>
                <p className="text-sm text-white/60 mt-2">We respond within 24 hours</p>
              </div>

              {/* Phone */}
              <div className="p-8 bg-[#8b5cf6]/10 rounded-xl border border-[#8b5cf6]/20">
                <div className="text-5xl mb-4">📞</div>
                <h3 className="text-xl font-bold mb-2 text-[#8b5cf6]">Call Us</h3>
                <a 
                  href="tel:+1-555-FORENSIC"
                  className="text-[#8b5cf6] text-lg font-bold no-underline hover:text-[#a78bfa] transition-colors"
                >
                  +1 (555) FORENSIC
                </a>
                <p className="text-sm text-white/60 mt-2">Monday - Friday, 9 AM - 6 PM EST</p>
              </div>
            </div>

            {/* What to Expect */}
            <div className="p-8 bg-white/5 rounded-xl border border-white/10 mb-12">
              <h3 className="text-lg sm:text-xl font-bold mb-4 text-white">What happens next?</h3>
              <div className="grid gap-4 text-left">
                <div className="flex items-center gap-4">
                  <span className="text-2xl">1️⃣</span>
                  <span className="text-base text-white/80">We'll discuss your specific needs and current challenges</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-2xl">2️⃣</span>
                  <span className="text-base text-white/80">Schedule a personalized demo of WhyDidItDie</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-2xl">3️⃣</span>
                  <span className="text-base text-white/80">Get custom pricing and implementation timeline</span>
                </div>
              </div>
            </div>

            {/* Back to Home */}
            <div className="pt-8 border-t border-white/10">
              <a 
                href="https://app.whydiditdie.com/" 
                className="text-[#e07a5f] text-base sm:text-lg no-underline hover:text-[#ff6b6b] hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
              >
                ← Back to Home
              </a>
            </div>
          </div>
        </div>

        {/* Footer with Social Media Handles */}
        <footer className="py-8 px-4 sm:px-8 border-t border-white/10">
          <div className="w-full max-w-screen-sm mx-auto text-center">
            <h4 className="text-base font-bold text-white mb-4">Follow Us</h4>
            <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4 justify-center">
              <a
                href="https://www.instagram.com/ai_whydiditdie"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 p-2 bg-white/10 rounded-lg border border-white/20 hover:bg-[#e07a5f]/20 hover:border-[#e07a5f]/50 transition-all"
              >
                <svg className="w-5 h-5 text-[#e07a5f] group-hover:text-[#ff6b6b]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.326 3.608 1.301.975.975 1.24 2.242 1.301 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.326 2.633-1.301 3.608-.975.975-2.242 1.24-3.608 1.301-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.326-3.608-1.301-.975-.975-1.24-2.242-1.301-3.608-.058-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.062-1.366.326-2.633 1.301-3.608.975-.975 2.242-1.24 3.608-1.301 1.266-.058 1.646-.07 4.85-.07m0-2.163c-3.259 0-3.667.014-4.947.072-1.524.069-2.978.377-4.103 1.502-1.125 1.125-1.433 2.579-1.502 4.103-.058 1.28-.072 1.688-.072 4.947s.014 3.667.072 4.947c.069 1.524.377 2.978 1.502 4.103 1.125 1.125 2.579 1.433 4.103 1.502 1.28.058 1.688.072 4.947.072s3.667-.014 4.947-.072c1.524-.069 2.978-.377 4.103-1.502 1.125-1.125 1.433-2.579 1.502-4.103.058-1.28.072-1.688.072-4.947s-.014-3.667-.072-4.947c-.069-1.524-.377-2.978-1.502-4.103-1.125-1.125-2.579-1.433-4.103-1.502-1.28-.058-1.688-.072-4.947-.072z"/>
                  <path d="M12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zm0 10.162a4 4 0 110-8 4 4 0 010 8z"/>
                  <circle cx="18.406" cy="5.594" r="1.44"/>
                </svg>
                <span className="text-sm text-white/90 group-hover:text-[#ff6b6b]">@ai_whydiditdie</span>
              </a>
              <a
                href="https://x.com/whydiditdie"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 p-2 bg-white/10 rounded-lg border border-white/20 hover:bg-[#e07a5f]/20 hover:border-[#e07a5f]/50 transition-all"
              >
                <svg className="w-5 h-5 text-[#e07a5f] group-hover:text-[#ff6b6b]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zM16.62 20.61h2.048L7.353 3.39H5.154l11.466 17.22z"/>
                </svg>
                <span className="text-sm text-white/90 group-hover:text-[#ff6b6b]">@whydiditdie</span>
              </a>
              <a
                href="https://www.facebook.com/whydiditdie"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 p-2 bg-white/10 rounded-lg border border-white/20 hover:bg-[#e07a5f]/20 hover:border-[#e07a5f]/50 transition-all"
              >
                <svg className="w-5 h-5 text-[#e07a5f] group-hover:text-[#ff6b6b]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="text-sm text-white/90 group-hover:text-[#ff6b6b]">@whydiditdie</span>
              </a>
              <a
                href="https://www.tiktok.com/@app.whydiditdie"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 p-2 bg-white/10 rounded-lg border border-white/20 hover:bg-[#e07a5f]/20 hover:border-[#e07a5f]/50 transition-all"
              >
                <svg className="w-5 h-5 text-[#e07a5f] group-hover:text-[#ff6b6b]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69c-.93-.91-2.17-1.49-3.54-1.62v-2.1h-2.64v11.28c0 1.38-1.12 2.5-2.5 2.5s-2.5-1.12-2.5-2.5 1.12-2.5 2.5-2.5c.24 0 .47.04.69.11v-2.88c-.22-.02-.44-.03-.67-.03-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5V8.64c1.33.53 2.81.84 4.31.84V6.69z"/>
                </svg>
                <span className="text-sm text-white/90 group-hover:text-[#ff6b6b]">@app.whydiditdie</span>
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ContactSalesPage;